// Shareable category keys (demographics/genderAgeRange is always included)
export type EmergencyCategory =
  | 'allergies'
  | 'medications'
  | 'conditions'
  | 'surgicalHistory'
  | 'vaccineHistory'
  | 'emergencyContacts'
  | 'familyDoctor';

export const ALL_EMERGENCY_CATEGORIES: { key: EmergencyCategory; label: string }[] = [
  { key: 'allergies', label: 'Allergies' },
  { key: 'medications', label: 'Current Medications' },
  { key: 'conditions', label: 'Active Conditions' },
  { key: 'surgicalHistory', label: 'Surgical History' },
  { key: 'vaccineHistory', label: 'Vaccine History' },
  { key: 'emergencyContacts', label: 'Emergency Contacts' },
  { key: 'familyDoctor', label: 'Family Doctor' },
];

// Categories recommended to always share (warning shown if deselected)
export const RECOMMENDED_CATEGORIES: EmergencyCategory[] = [
  'allergies',
  'medications',
  'conditions',
];

export interface EmergencyDemographics {
  gender: string | null;
  ageRange: string | null;
  bloodType: string | null;
}

// De-identified health data shown to emergency responders
export interface EmergencyHealthData {
  demographics?: EmergencyDemographics;
  genderAgeRange?: string; // legacy fallback
  allergies?: EmergencyAllergy[];
  medications?: EmergencyMedication[];
  conditions?: string[];
  surgicalHistory?: EmergencySurgery[];
  vaccineHistory?: EmergencyVaccine[];
  emergencyContacts?: EmergencyContactInfo[];
  familyDoctor?: EmergencyDoctorInfo | null;
}

export interface EmergencyAllergy {
  allergen: string;
  reactionType: string;
  severity: string;
}

export interface EmergencyMedication {
  name: string;
  dosageFrequency: string;
}

export interface EmergencySurgery {
  procedure: string;
  date: string;
}

export interface EmergencyVaccine {
  vaccine: string;
  date: string;
}

export interface EmergencyContactInfo {
  name: string;
  relationship: string;
  phone: string;
}

export interface EmergencyDoctorInfo {
  name: string;
  phone: string;
}

// Token and code types
export interface EmergencyToken {
  accessId: string;    // 8-character alphanumeric ID
  pin: string;         // 6-digit PIN (only returned on generation, never stored plain)
  token: string;       // 32-char hex token (only returned on generation)
  qrUrl: string;       // Full URL for QR code
  status: 'active' | 'used' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string;
  usedAt?: string;
}

export interface EmergencyTokenStatus {
  hasActiveToken: boolean;
  token?: {
    accessId: string;
    status: 'active' | 'used' | 'revoked' | 'expired';
    createdAt: string;
    expiresAt: string;
    usedAt?: string;
    sharedCategories?: EmergencyCategory[];
  };
}

// Audit log
export interface EmergencyAuditEntry {
  timestamp: string;
  action: 'generated' | 'accessed' | 'revoked' | 'expired' | 'failed_attempt';
  method?: 'qr' | 'pin';
  ipAddress?: string;
  userAgent?: string;
  accessId?: string;
}

// API request/response types
export interface GenerateEmergencyCodeResponse {
  accessId: string;
  pin: string;
  token: string;
  qrUrl: string;
  expiresAt: string;
  sharedCategories?: EmergencyCategory[];
}

export interface ValidateEmergencyRequest {
  token?: string;       // For QR-based access
  accessId?: string;    // For PIN-based access
  pin?: string;         // For PIN-based access
}

export interface ValidateEmergencyResponse {
  success: boolean;
  data?: EmergencyHealthData;
  error?: string;
}

export interface EmergencyAuditResponse {
  entries: EmergencyAuditEntry[];
}

// Admin types
export interface AdminEmergencyStatus {
  username: string;
  email?: string;
  hasActiveToken: boolean;
  tokenStatus?: string;
  accessId?: string;
  createdAt?: string;
  expiresAt?: string;
  usedAt?: string;
}
